export type SecuritySection = {
  id: string;
  title: string;
  lead: string;
  paragraphs?: string[];
  /** Trusted static HTML from this file only (e.g. legal cross-links). */
  htmlParagraphs?: string[];
  bullets?: string[];
  subsections?: Array<{ heading: string; paragraphs?: string[]; bullets?: string[] }>;
};

export type SecurityNavItem = { id: string; label: string };

export const SECURITY_EFFECTIVE_DATE = "Effective Date: June 12, 2026";

export const SECURITY_PAGE_SUBTITLE =
  "How Email Perfect protects your account, data, and privacy — including cookies and subprocessors.";

export const SECURITY_NAV: SecurityNavItem[] = [
  { id: "overview", label: "Overview" },
  { id: "commitments", label: "Our commitments" },
  { id: "hosting", label: "Hosting" },
  { id: "encryption", label: "Encryption" },
  { id: "authentication", label: "Authentication" },
  { id: "access-controls", label: "Access controls" },
  { id: "backups", label: "Backups" },
  { id: "data-privacy", label: "Data privacy" },
  { id: "cookies", label: "Cookies" },
  { id: "monitoring", label: "Monitoring" },
  { id: "incident-response", label: "Incident response" },
  { id: "subprocessors", label: "Subprocessors" },
  { id: "faq", label: "Security FAQ" },
  { id: "contact", label: "Contact" },
];

export const securitySections: SecuritySection[] = [
  {
    id: "overview",
    title: "Security at Email Perfect",
    lead:
      "Email Perfect is designed with security and privacy in mind. This page summarizes our practices for customers, procurement teams, and anyone reviewing how we handle data.",
    paragraphs: [
      "We use passwordless authentication, role-based access for teams and projects, and minimal first-party cookies. We do not sell customer data or use customer content to train AI models.",
      "Report security issues to security@emailperfect.io. For privacy rights, retention, and showcase consent, see our Privacy Policy. For contractual terms, see our Terms of Service.",
    ],
    htmlParagraphs: [
      'Read our <a data-legal-static href="/privacy">Privacy Policy</a> and <a data-legal-static href="/terms">Terms of Service</a>.',
    ],
  },
  {
    id: "commitments",
    title: "Our commitments",
    lead: "Permanent privacy and security commitments to our customers.",
    bullets: [
      "We never sell customer data",
      "We never train AI models on customer content",
      "Account deletion and data export are available on request",
      "A Data Processing Addendum (DPA) is available on request",
      "Support access to customer content is limited to support and hypercare needs",
      "Subprocessor transparency at /subprocessors",
    ],
  },
  {
    id: "hosting",
    title: "Hosting",
    lead: "Production infrastructure runs in the United States.",
    paragraphs: [
      "Email Perfect is hosted in the United States on Namecheap managed hosting (cPanel). Customer data is stored on production servers separate from source code deploys.",
    ],
  },
  {
    id: "encryption",
    title: "Encryption",
    lead: "Data in transit and at rest is protected using industry-standard controls.",
    bullets: [
      "TLS 1.2 or higher for all API and application traffic",
      "Hosting-provider storage controls for data at rest on production servers",
      "Encrypted off-site backup archives (AES-256)",
    ],
  },
  {
    id: "authentication",
    title: "Authentication",
    lead: "We do not store passwords.",
    bullets: [
      "Magic-link email authentication",
      "Google Sign-In support",
      "No password storage on our systems",
      "Administrative access is protected using multi-factor authentication via Google Sign-In",
    ],
  },
  {
    id: "access-controls",
    title: "Access controls",
    lead: "Collaboration permissions are enforced at the team and project level.",
    bullets: [
      "Role-based access controls (owner, editor, viewer, and project-specific roles)",
      "Team-level permissions",
      "Project-level permissions and sharing invites",
    ],
  },
  {
    id: "backups",
    title: "Backups",
    lead: "Production data is protected with regular encrypted backups.",
    paragraphs: [
      "Production data is backed up regularly and stored separately from production infrastructure. Backups are encrypted before storage and retained on a rolling schedule.",
    ],
  },
  {
    id: "data-privacy",
    title: "Data privacy",
    lead: "You control your account data and can request export or deletion.",
    bullets: [
      "GDPR-ready privacy controls",
      "Account deletion available from Account settings",
      "Data export available upon request at support@emailperfect.io",
      "Data Processing Addendum (DPA) available upon request at privacy@emailperfect.io",
    ],
  },
  {
    id: "cookies",
    title: "Cookies",
    lead:
      "Email Perfect uses a minimal set of cookies. We do not use analytics cookies, advertising cookies, remarketing technologies, or behavioral profiling cookies.",
    subsections: [
      {
        heading: "What we do not use",
        bullets: [
          "Analytics cookies (e.g. Google Analytics)",
          "Advertising or remarketing cookies",
          "Behavioral profiling or cross-site tracking cookies",
        ],
      },
      {
        heading: "Cookies we use",
        paragraphs: ["We set only strictly necessary first-party cookies:"],
        bullets: [
          "ep_session — keeps you signed in (HttpOnly, SameSite=Lax, up to 30 days)",
          "ep_invite_token — holds a pending team invite while you complete sign-in (HttpOnly, up to 24 hours)",
        ],
      },
      {
        heading: "Local storage",
        paragraphs: [
          "The application also uses browser local storage for in-app functionality such as draft work, undo/redo, and sign-in convenience. Local storage is not a cookie. See our Privacy Policy for details.",
        ],
      },
      {
        heading: "Third-party resources",
        paragraphs: [
          "When you choose to use certain features, third-party services may set their own cookies on their domains, including Google Sign-In, optional Canva media integration, and Google Fonts loaded in the editor. Those services are governed by their own policies.",
        ],
      },
      {
        heading: "Cookie banner",
        paragraphs: [
          "Because our first-party cookies are strictly necessary for authentication and invite completion, we do not display a cookie consent banner today.",
        ],
      },
      {
        heading: "Your choices",
        bullets: [
          "Sign out to clear your session cookie",
          "Use browser settings to block or delete cookies (sign-in and invites may not work)",
        ],
      },
    ],
  },
  {
    id: "monitoring",
    title: "Monitoring",
    lead: "We monitor service availability and maintain operational logs.",
    paragraphs: [
      "Uptime monitoring and alerting for service availability; operational logging for security investigations.",
    ],
  },
  {
    id: "incident-response",
    title: "Incident response",
    lead: "We investigate security incidents promptly and notify customers when appropriate.",
    paragraphs: [
      "Email Perfect maintains a documented incident response process and will notify affected customers of confirmed security incidents where appropriate.",
      "We have no material security breaches to disclose as of June 2026.",
    ],
  },
  {
    id: "subprocessors",
    title: "Subprocessors",
    lead: "We use vetted third-party providers to operate the platform.",
    paragraphs: [
      "Email Perfect uses third-party service providers for hosting, authentication, email delivery, and optional integrations.",
    ],
    htmlParagraphs: [
      'See our full <a data-legal-static href="/subprocessors">Subprocessors</a> list for vendors and purposes.',
    ],
  },
  {
    id: "faq",
    title: "Security FAQ",
    lead: "Quick answers to common security and privacy questions.",
    bullets: [
      "Do you sell customer data? No.",
      "Do you use customer content to train AI models? No.",
      "Are passwords stored? No. Email Perfect uses passwordless authentication.",
      "Can I delete my data? Yes — from Account settings.",
      "Can I export my data? Yes, upon request at support@emailperfect.io.",
      "Do you offer a DPA? Yes, upon request at privacy@emailperfect.io.",
      "Is Email Perfect SOC 2 certified? Not at this time.",
    ],
  },
  {
    id: "contact",
    title: "Contact",
    lead: "Reach our team for security, privacy, or compliance questions.",
    paragraphs: ["security@emailperfect.io · privacy@emailperfect.io"],
  },
];

export function getSecuritySectionById(id: string): SecuritySection | undefined {
  return securitySections.find((section) => section.id === id);
}
